/**
 * Access JWT from the last successful refresh or login.
 *
 * Kept in memory only. Browser persistence belongs exclusively to httpOnly
 * cookies; browser-readable storage turns an XSS into an exportable bearer
 * credential. Reloads recover through the refresh cookie.
 */

const SESSION_ACCESS_STORAGE_KEY = 'jester_terminal_access';
export const TERMINAL_ACCESS_HEADER = 'X-Artifice-Access';
export const TERMINAL_ACCESS_HEADER_ARTIFICE = TERMINAL_ACCESS_HEADER;
/** @deprecated Transitional header for API deployments and clients that still use the Jester contract. */
export const TERMINAL_ACCESS_HEADER_JESTER = 'X-Jester-Access';

function purgeStoredSessionAccessToken(): void {
  try {
    sessionStorage.removeItem(SESSION_ACCESS_STORAGE_KEY);
  } catch {
    /* ignore unavailable storage */
  }
}

purgeStoredSessionAccessToken();
let sessionAccessToken: string | null = null;
/** Bumped on logout so an in-flight refresh cannot store a token after sign-out. */
let sessionAccessGeneration = 0;

export function getSessionAccessGeneration(): number {
  return sessionAccessGeneration;
}

export function rememberSessionAccessToken(
  token: string | null | undefined,
  generation?: number,
): void {
  if (generation != null && generation !== sessionAccessGeneration) return;
  const next = String(token || '').trim();
  sessionAccessToken = next || null;
  purgeStoredSessionAccessToken();
}

export function readSessionAccessToken(): string | null {
  return sessionAccessToken;
}

export function clearSessionAccessToken(): void {
  sessionAccessToken = null;
  sessionAccessGeneration += 1;
  purgeStoredSessionAccessToken();
}

/** Memory/session token from refresh/login, then any leftover JS mirror (non-Oroboros). */
function readJwtExpMs(token: string): number | null {
  try {
    const part = String(token || '').split('.')[1] || '';
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad)) as { exp?: unknown };
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True when the in-tab access JWT still has a few seconds left. */
export function isLiveSessionAccessToken(token?: string | null): boolean {
  const raw = String(token || readSessionAccessToken() || '').trim();
  if (!raw || raw.split('.').length !== 3) return false;
  const expMs = readJwtExpMs(raw);
  return expMs != null && expMs - Date.now() > 5_000;
}

/** Mini App must not send Telegram initData while this tab already has a live JWT. */
export function shouldSendTelegramInitData(initDataPresent: boolean): boolean {
  return Boolean(initDataPresent) && !isLiveSessionAccessToken();
}

export function readLiveAccessToken(): string | null {
  const mem = readSessionAccessToken();
  if (mem) return mem;
  try {
    const legacy = String((window as any)?.JesterApp?.authToken || '').trim();
    if (legacy) return legacy;
  } catch {
    /* ignore */
  }
  return null;
}

function headersToRecord(headers?: HeadersInit | Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  if (!headers) return out;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      if (value) out[key] = value;
    }
    return out;
  }
  for (const [key, value] of Object.entries(headers as Record<string, string | undefined>)) {
    if (typeof value === 'string' && value) out[key] = value;
  }
  return out;
}

/**
 * Copy headers and attach the in-tab access JWT as canonical
 * `X-Artifice-Access` plus the transitional `X-Jester-Access` alias.
 *
 * Keep the app JWT out of `Authorization: Bearer` so that header remains
 * available to hosting/platform authentication. Cookie session plus the
 * namespaced access headers is what `requireAuth` accepts.
 */
export function withSessionAccessHeaders(
  headers?: HeadersInit | Record<string, string | undefined>,
): Record<string, string> {
  const out = headersToRecord(headers);
  const hasJester = Object.keys(out).some((key) => key.toLowerCase() === 'x-jester-access');
  const hasArtifice = Object.keys(out).some((key) => key.toLowerCase() === 'x-artifice-access');
  const access = readLiveAccessToken();
  if (access) {
    if (!hasArtifice) out[TERMINAL_ACCESS_HEADER] = access;
    if (!hasJester) out[TERMINAL_ACCESS_HEADER_JESTER] = access;
  }
  return out;
}

/**
 * Session JWT first. If this tab has no live access token, attach a Mini App
 * param-compare JWT on the same Artifice/Jester headers — never Authorization.
 */
export function withParamOrSessionAccessHeaders(
  headers?: HeadersInit | Record<string, string | undefined>,
  paramToken?: string | null,
): Record<string, string> {
  const out = withSessionAccessHeaders(headers);
  if (readLiveAccessToken()) return out;
  const param = String(paramToken || '').trim();
  if (!param) return out;
  const hasArtifice = Object.keys(out).some((key) => key.toLowerCase() === 'x-artifice-access');
  const hasJester = Object.keys(out).some((key) => key.toLowerCase() === 'x-jester-access');
  if (!hasArtifice) out[TERMINAL_ACCESS_HEADER] = param;
  if (!hasJester) out[TERMINAL_ACCESS_HEADER_JESTER] = param;
  return out;
}
